import { Action, ActionReducer, MetaReducer } from '@ngrx/store';
import { FlowTypes } from '@shared/models';
import { getConfigClientFromRTE } from '@src/app/core/config/config-client';
import * as AdaptedStateActions from '@store/actions/adapted-state/adapted-state.actions';
import { merge } from 'lodash';
import { localStorageSync } from 'ngrx-store-localstorage';

function getSavedState(localStorageKey: string): any {
  return JSON.parse(localStorage.getItem(localStorageKey));
}

// Cypress testing : the local storage key to pull the state object from
const key = '__app_storage__';

export function storageMetaReducer<S, A extends Action = Action>(reducer: ActionReducer<S, A>) {
  return function (state: S, action: A): S {
    const nextState = reducer(state, action);
    // @ts-ignore
    if (window.Cypress && action.type !== AdaptedStateActions.DeleteTransactionState.type) {
      const savedState = getSavedState(key);
      if (savedState) {
        // Preserve MFE state slices that update dynamically via custom element events
        const { mfePersonalAddress, ...restSavedState } = savedState;
        merge(nextState, restSavedState);
      }
    }
    return nextState;
  };
}

export function sessionStorageSyncReducer(reducer: ActionReducer<any>): ActionReducer<any> {
  const isProd = getConfigClientFromRTE().isProduction;
  const sessionKeys = [
    'triage',
    'internalTransfer',
    'costBasis',
    'itm',
    'termsAccordion',
    'gifting',
    'selectedClientAccounts',
    {
      documentUpload: [
        'filesMetaData',
        'clearData',
        'accountTypes',
        'wldStandardDocuments',
        'wldLetterDocuments',
        'wldTaxDocuments',
      ],
    },
  ];
  const inheritanceKeys = [
    'transactions',
    'currentTransactionId',
    'decedentInfo',
    'taxDocumentUpload',
    'openAccount',
    'incomeNetWorth',
    'investmentObjectives',
    'mfePersonalAddress',
    'mfePhone',
    'isMultiReviewPageReachedAndEditMode',
    'isMultiOAReviewPageReachedAndEditMode',
    'isMultiReviewPageReachedAndSkipInvestmentRevision',
    'tcfTrust',
    'trust',
    'trustTaxInfo',
    'clientAccounts',
    'letterUpload',
  ];
  const satOnlyKeys = [];
  const keys = isProd ? [...sessionKeys] : [...sessionKeys, ...satOnlyKeys];

  const commonSync = localStorageSync({
    keys,
    rehydrate: true,
    removeOnUndefined: false,
    storage: sessionStorage,
  });

  const inheritanceSync = localStorageSync({
    keys: inheritanceKeys,
    rehydrate: true,
    removeOnUndefined: false,
    restoreDates: false,
    storage: sessionStorage,
    syncCondition: (state) => {
      return (
        state.featureToggles?.cwp_inheritance_save_state &&
        (state.triage?.flowType === FlowTypes.MULTI_INHERITANCE ||
          state.triage?.flowType === FlowTypes.INHERITANCE)
      );
    },
  });

  return inheritanceSync(commonSync(reducer));
}

export const metaReducers: MetaReducer<any>[] = [storageMetaReducer, sessionStorageSyncReducer];
